import { Component, ElementRef, Input, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { TranslateService } from '@ngx-translate/core';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faPaperclip,
  faFile,
  faFilePdf,
  faFileWord,
  faFileExcel,
  faFileImage,
  faFileArchive
} from '@fortawesome/free-solid-svg-icons';
import { StoredFile } from '../../../interfaces/storedfile.interface';

@Component({
    selector: 'file-upload',
    templateUrl: './file-upload.component.html',
    styleUrls: ['./file-upload.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FileUploadComponent {
  constructor(
    protected spinner: NgxSpinnerService,
    protected translate: TranslateService
  ) {}

  @Input() readOnly = false;
  @Input() existingFiles: StoredFile[] = [];

  public files: File[] = [];
  public errorText: string | null = null;
  public isDragOver = false;
  public faPaperclip = faPaperclip;

  @ViewChild('fileUpload') fileInput!: ElementRef<HTMLInputElement>;

  public onFileSelected(event: Event): void {
    if (this.readOnly) {
      return;
    }

    const input = event.target as HTMLInputElement;
    const selectedFiles = input.files;

    if (!selectedFiles || selectedFiles.length === 0) {
      return;
    }

    this.addFiles(Array.from(selectedFiles));
    input.value = '';
  }

  public onDragOver(event: DragEvent): void {
    if (this.readOnly) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  public onDragLeave(event: DragEvent): void {
    if (this.readOnly) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  public onDrop(event: DragEvent): void {
    if (this.readOnly) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const droppedFiles = event.dataTransfer?.files;

    if (!droppedFiles || droppedFiles.length === 0) {
      return;
    }

    this.addFiles(Array.from(droppedFiles));
  }

  public removeFile(index: number): void {
    if (this.readOnly) {
      return;
    }

    this.files.splice(index, 1);
    this.files = [...this.files];
    this.errorText = null;
  }

  public clear(): void {
    if (this.readOnly) {
      return;
    }

    this.files = [];
    this.errorText = null;
    this.isDragOver = false;

    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  public getFilePresentation(fileNameRaw: string): { icon: IconDefinition; class: string } {
    const fileName = (fileNameRaw || '').toLowerCase();

    if (fileName.endsWith('.pdf')) {
      return { icon: faFilePdf, class: 'file-pdf' };
    }

    if (
      fileName.endsWith('.doc') ||
      fileName.endsWith('.docx') ||
      fileName.endsWith('.odt')
    ) {
      return { icon: faFileWord, class: 'file-word' };
    }

    if (
      fileName.endsWith('.xls') ||
      fileName.endsWith('.xlsx') ||
      fileName.endsWith('.csv')
    ) {
      return { icon: faFileExcel, class: 'file-excel' };
    }

    if (
      fileName.endsWith('.png') ||
      fileName.endsWith('.jpg') ||
      fileName.endsWith('.jpeg') ||
      fileName.endsWith('.gif') ||
      fileName.endsWith('.webp') ||
      fileName.endsWith('.svg')
    ) {
      return { icon: faFileImage, class: 'file-image' };
    }

    if (
      fileName.endsWith('.zip') ||
      fileName.endsWith('.rar') ||
      fileName.endsWith('.7z')
    ) {
      return { icon: faFileArchive, class: 'file-archive' };
    }

    return { icon: faFile, class: 'file-default' };
  }

  public addFiles(incomingFiles: File[]): void {
    if (this.readOnly) {
      return;
    }

    const filesWithoutExtension = incomingFiles.filter(incomingFile => !this.hasFileExtension(incomingFile.name));

    if (filesWithoutExtension.length > 0) {
      const fileNames = filesWithoutExtension.map(fileWithoutExtension => `'${fileWithoutExtension.name}'`).join(', ');
      this.errorText = filesWithoutExtension.length === 1
        ? this.translate.instant('files.missingExtension', { fileNames })
        : this.translate.instant('files.missingExtensionPlural', { fileNames });
      return;
    }

    const mergedFiles = [...this.files];

    for (const incomingFile of incomingFiles) {
      const alreadyExists = mergedFiles.some(existingFile =>
        existingFile.name === incomingFile.name &&
        existingFile.size === incomingFile.size &&
        existingFile.lastModified === incomingFile.lastModified
      );

      if (!alreadyExists) {
        mergedFiles.push(incomingFile);
      }
    }

    if (mergedFiles.length > 10) {
      this.errorText = this.translate.instant('files.tooManyFiles');
      return;
    }

    const totalSize = mergedFiles.reduce((sum, file) => sum + file.size, 0);

    if (totalSize > 500_000_000) {
      this.errorText = this.translate.instant('files.tooLarge');
      return;
    }

    this.files = mergedFiles;
    this.errorText = null;
  }

  private hasFileExtension(fileName: string): boolean {
    const lastDotIndex = fileName.lastIndexOf('.');
    return lastDotIndex !== -1 && lastDotIndex !== fileName.length - 1;
  }
}